import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { FaFileImage, FaSpinner } from "react-icons/fa";
import { MdClose } from "react-icons/md";

const uploadToCloudinary = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", "dev-wb");
  formData.append("cloud_name", "dqxlte6wf");

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/dqxlte6wf/image/upload`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.secure_url;
  } catch (error) {
    console.error("Cloudinary upload error:", error);
    throw error;
  }
};

const AdminProductModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = {},
  isEditMode,
  isSubmitting,
}) => {
  const defaultForm = { name: "", description: "", images: [] };
  const defaultInfoSection = [{ heading: "", content: "", points: [""] }];

  const [formData, setFormData] = useState(defaultForm);
  const [imageArray, setImageArray] = useState([]);
  const [infoSection, setInfoSection] = useState(defaultInfoSection);
  const [anyImageUploading, setAnyImageUploading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setFormData(defaultForm);
      setInfoSection(defaultInfoSection);
      setImageArray([]);
      return;
    }

    if (!isEditMode || !initialData) {
      setFormData(defaultForm);
      setInfoSection(defaultInfoSection);
      setImageArray([]);
      return;
    }

    setFormData({ ...defaultForm, ...initialData });

    const formattedInfoSection =
      initialData.infoSection?.map((section) => ({
        heading: section.heading || "",
        content: section.content || "",
        points: section.points?.length ? section.points : [""],
      })) || defaultInfoSection;

    setInfoSection(formattedInfoSection);

    const formattedImages = Array.isArray(initialData.images)
      ? initialData.images.map((url, index) => ({
          id: `existing-${index}`,
          url,
          preview: url,
          isUploading: false,
        }))
      : [];
    setImageArray(formattedImages);
  }, [isOpen, initialData, isEditMode]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const oversizedFiles = files.filter((file) => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert(`Some files are too large. Maximum size is 10MB per image.`);
      return;
    }

    const placeholders = files.map((file) => ({
      id: `temp-${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
      isUploading: true,
      url: null,
    }));

    setImageArray((prev) => [...prev, ...placeholders]);
    setAnyImageUploading(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const placeholderId = placeholders[i].id;

      try {
        const uploadedUrl = await uploadToCloudinary(file);

        if (uploadedUrl) {
          setImageArray((prev) =>
            prev.map((img) =>
              img.id === placeholderId
                ? { ...img, url: uploadedUrl, isUploading: false }
                : img
            )
          );
        } else {
          setImageArray((prev) =>
            prev.filter((img) => img.id !== placeholderId)
          );
        }
      } catch (error) {
        console.error("Upload failed for file:", file.name, error);

        setImageArray((prev) => prev.filter((img) => img.id !== placeholderId));
      }
    }

    setAnyImageUploading(false);
    e.target.value = null;
  };

  const removeImage = (index) => {
    const imageToRemove = imageArray[index];
    if (imageToRemove?.preview && imageToRemove.preview.startsWith("blob:")) {
      URL.revokeObjectURL(imageToRemove.preview);
    }

    setImageArray((prev) => prev.filter((_, i) => i !== index));
  };

  const addMoreInfoSection = () => {
    setInfoSection((prev) => [
      ...prev,
      { heading: "", content: "", points: [""] },
    ]);
  };

  const removeInfoSection = (index) => {
    setInfoSection((prev) => prev.filter((_, i) => i !== index));
  };

  const handleInfoSectionChange = (e, index) => {
    const { name, value } = e.target;
    setInfoSection((prev) =>
      prev.map((sec, i) => (i === index ? { ...sec, [name]: value } : sec))
    );
  };

  const addPoint = (sectionIndex) => {
    setInfoSection((prev) =>
      prev.map((sec, i) =>
        i === sectionIndex ? { ...sec, points: [...sec.points, ""] } : sec
      )
    );
  };

  const removePoint = (sectionIndex, pointIndex) => {
    setInfoSection((prev) =>
      prev.map((sec, i) =>
        i === sectionIndex
          ? {
              ...sec,
              points: sec.points.filter((_, idx) => idx !== pointIndex),
            }
          : sec
      )
    );
  };

  // HANDLE POINT CHANGE
  const handlePointChange = (e, sectionIndex, pointIndex) => {
    const { value } = e.target;
    setInfoSection((prev) =>
      prev.map((sec, i) =>
        i === sectionIndex
          ? {
              ...sec,
              points: sec.points.map((p, idx) =>
                idx === pointIndex ? value : p
              ),
            }
          : sec
      )
    );
  };

  // HANDLE FORM SUBMIT
  const handleSubmit = (e) => {
    e.preventDefault();

    // Check if any images are still uploading
    if (anyImageUploading) {
      alert(
        "Please wait for all images to finish uploading before submitting."
      );
      return;
    }

    // Get all successfully uploaded image URLs
    const uploadedImageUrls = imageArray
      .filter((img) => img.url && !img.isUploading)
      .map((img) => img.url);

    const formattedInfoSection = infoSection.map((sec) => ({
      heading: sec.heading,
      content: sec.content,
      points: sec.points.filter((p) => p.trim() !== ""),
    }));

    onSubmit({
      ...formData,
      images: uploadedImageUrls,
      infoSection: formattedInfoSection,
    });
  };

  if (!isOpen) return null;

  // Render
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
    >
      <div className="bg-white w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl px-6 pb-6 rounded-lg shadow-xl relative max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white z-20 py-4">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="absolute top-4 right-3 text-gray-700 flex items-center justify-center z-30"
          >
            <MdClose className="w-8 h-8" />
          </button>
          <h2 className="text-2xl font-bold mb-4">
            {isEditMode ? "Edit Product" : "Add New Product"}
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name & Description */}
          <div>
            <label className="block font-medium mb-1">Product Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30 resize-y"
              placeholder="Write a detailed description..."
              required
            />
          </div>

          {/* Images */}
          <div>
            <label className="block font-medium mb-1">
              Product Images{" "}
              <span className="text-xs text-amber-600">(Max size 10mb)</span>
            </label>
            <div className="flex items-start gap-4">
              <button
                type="button"
                onClick={() =>
                  document.getElementById("image-upload-product")?.click()
                }
                className="flex flex-col items-center justify-center w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg hover:border-primaryColor transition-colors"
                disabled={anyImageUploading}
              >
                <FaFileImage className="text-2xl text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">
                  {imageArray.length > 0 ? "Add More" : "Upload Images"}
                </span>
              </button>
              <input
                id="image-upload-product"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                className="hidden"
                disabled={anyImageUploading}
              />
              {imageArray.length > 0 && (
                <div className="flex-1 overflow-x-auto pb-2">
                  <div className="flex gap-3">
                    {imageArray.map((image, idx) => (
                      <div
                        key={image.id || idx}
                        className="relative flex-shrink-0 w-32 h-24"
                      >
                        <img
                          src={image.preview || image.url}
                          alt={`Upload ${idx}`}
                          className="w-full h-full object-cover rounded border"
                        />
                        {image.isUploading && (
                          <div className="absolute inset-0 bg-black bg-opacity-40 flex justify-center items-center z-10">
                            <FaSpinner className="text-white animate-spin" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-5 h-5 pb-0.5 flex items-center justify-center shadow-md hover:bg-red-700 transition-colors"
                          disabled={image.isUploading}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {anyImageUploading && (
              <p className="text-sm text-amber-600 mt-2">
                Uploading images... Please wait before submitting.
              </p>
            )}
          </div>

          {/* Info Sections */}
          <div>
            <label className="block font-semibold mb-1">Product Details</label>
            {infoSection.map((section, sIdx) => (
              <div
                key={sIdx}
                className="border-2 rounded outline-primaryColor/30 flex flex-col px-4 mt-4 bg-white shadow-sm relative"
              >
                <div className="flex justify-between items-center pt-4 px-4">
                  <h4 className="font-semibold text-md text-primaryColor">
                    Detail Section {sIdx + 1}
                  </h4>
                  {infoSection.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeInfoSection(sIdx)}
                      className="text-sm text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="border-b-2 outline-primaryColor/30 flex flex-col px-4 py-4 gap-y-3">
                  <div>
                    <label className="block font-medium text-sm mb-1">
                      Heading
                    </label>
                    <input
                      type="text"
                      name="heading"
                      value={section.heading}
                      onChange={(e) => handleInfoSectionChange(e, sIdx)}
                      className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30"
                      placeholder="e.g. Key Features"
                    />
                  </div>
                  <div>
                    <label className="block font-medium text-sm mb-1">
                      Content
                    </label>
                    <textarea
                      name="content"
                      value={section.content}
                      onChange={(e) => handleInfoSectionChange(e, sIdx)}
                      rows={3}
                      className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30 resize-y"
                      placeholder="Write detailed content..."
                    />
                  </div>
                  <div>
                    <label className="block font-medium text-sm mb-1">
                      Bullet Points
                    </label>
                    {section.points.map((point, pIdx) => (
                      <div key={pIdx} className="flex items-center gap-2 mb-2">
                        <input
                          type="text"
                          value={point}
                          onChange={(e) => handlePointChange(e, sIdx, pIdx)}
                          className="flex-1 px-3 py-2 border-2 rounded outline-primaryColor/30"
                          placeholder="e.g. Precision repairs, honest service"
                        />
                        {section.points.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePoint(sIdx, pIdx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addPoint(sIdx)}
                      className="text-sm text-primaryColor hover:text-primaryColor/80 mt-1"
                    >
                      + Add bullet point
                    </button>
                  </div>
                </div>
              </div>
            ))}
            <button
              onClick={addMoreInfoSection}
              type="button"
              className="font-semibold text-primaryColor text-right py-2 pt-6 px-2"
            >
              + Add more details
            </button>
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded border border-gray-400 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || anyImageUploading}
              className={`px-4 py-2 bg-green-600 ${
                isEditMode ? "w-40" : "w-20"
              } text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? (
                <FaSpinner className="text-white animate-spin mx-auto" />
              ) : isEditMode ? (
                "Save Changes"
              ) : (
                "Add"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AdminProductModal;
